/**
 * @author Vivek Kumar <vivek.kumar@314ecorp.com>
 * @description Refund Allocation Button
 */
import { useState } from 'react';
import _ from 'lodash';
import { Button, Flex, Form, Input, Modal } from 'antd';
import { ButtonProps } from 'antd/lib';

import useUpdatePaymentReconciliation from 'hooks/payments/mutate/useUpdatePaymentReconciliation';
import { formatAmount } from 'utils/accounts/allAccounts';
import fhirSystemMapping from 'app-constants/eligibility/fhirSystemMapping.json';
import { getRefundAllocationsPayload } from 'utils/payment/paymentAllocation';

const getSubmissionModal = ({
	data,
	amount,
	isDelete,
	refundReason,
}: {
	data: PatientPayment.IPaymentReconciliation;
	amount: number;
	isDelete: boolean;
	refundReason: string;
}): PatientPayment.IPaymentReconciliationPatchModal => {
	return {
		allocations: getRefundAllocationsPayload(data?.allocations),
		fhirResource: {
			...(data.fhirResource as Fhir.PaymentReconciliation),
			outcome: isDelete ? 'partial' : 'complete',
			returnedAmount: isDelete
				? undefined
				: { value: amount, currency: 'USD', extension: [{ url: fhirSystemMapping.refundReason, valueString: refundReason }] },
		},
	};
};

type IProps = {
	data: PatientPayment.IPaymentReconciliation;
	amount: number;
	id: string;
	reason?: string;
	isEdit?: boolean;
	renderTrigger?: (open: () => void) => React.ReactNode;
};

const RefundAllocation: React.FC<ButtonProps & IProps> = ({ id, data, amount, reason, isEdit, renderTrigger, ...rest }) => {
	const [open, setOpen] = useState(false);
	const [refundReason, setRefundReason] = useState(reason || '');
	const [isDelete, setIsDelete] = useState(false);

	const { mutate, isPending } = useUpdatePaymentReconciliation(id);

	const handleReset = () => {
		setRefundReason(reason || '');
		setOpen(false);
	};

	const handleSubmit = () => {
		setIsDelete(false);
		const modal = getSubmissionModal({ data, amount, isDelete: false, refundReason });
		mutate({ modal }, { onSuccess: () => setOpen(false) });
	};

	const handleDelete = () => {
		setIsDelete(true);
		const modal = getSubmissionModal({ data, amount, isDelete: true, refundReason });
		mutate({ modal }, { onSuccess: () => setOpen(false) });
	};

	const buttonText = isEdit ? <i>{'View'}</i> : `Record refund of ${formatAmount(amount)}`;

	return (
		<>
			{renderTrigger ? (
				renderTrigger(() => setOpen(true))
			) : (
				<Button type='link' onClick={() => setOpen(true)} {...rest}>
					{buttonText}
				</Button>
			)}
			<Modal
				title='Refund Details'
				destroyOnHidden
				open={open}
				onCancel={() => setOpen(false)}
				width={600}
				footer={
					<Flex justify='space-between' gap={16}>
						{isEdit && (
							<Button
								type='text'
								danger
								key='view'
								loading={isDelete && isPending}
								disabled={isPending}
								onClick={handleDelete}
							>
								{'Delete'}
							</Button>
						)}
						<Button key='cancel' variant='link' color='default' onClick={handleReset} className='ml-auto'>
							{'Cancel'}
						</Button>
						<Button
							key='submit'
							type='primary'
							onClick={() => handleSubmit()}
							loading={isPending && !isDelete}
							disabled={!refundReason || _.isEqual(refundReason, reason)}
						>
							{isEdit ? 'Save' : 'Record Refund'}
						</Button>
					</Flex>
				}
			>
				<Form layout='vertical' colon={false}>
					<Form.Item name='refundAmount' label='Amount ($)' rules={[{ required: true, message: 'Please enter refund amount' }]}>
						<Input type='number' defaultValue={amount} readOnly disabled />
					</Form.Item>
					<Form.Item name='refundReason' label='Reason' rules={[{ required: true, message: 'Please enter refund reason' }]}>
						<Input.TextArea
							placeholder='Enter refund reason'
							className='w-full'
							defaultValue={reason}
							onChange={(e) => setRefundReason(e.target.value)}
						/>
					</Form.Item>
				</Form>
			</Modal>
		</>
	);
};

export default RefundAllocation;
